import { defineStore } from 'pinia';
import {
    computed, shallowReadonly, ref,
    type Ref,
} from 'vue';
import ColorsApi from '@/scripts/api/colors/ColorsApi';
import type { ColorOption } from '@/scripts/types/color/ColorOption';

type ColorsStore = {
    colors: Readonly<Ref<ColorOption[]>>,
    defaultColor: Readonly<Ref<ColorOption>>,
    colorsDictionary: Readonly<Ref<Record<string, string>>>,
    hydrate: () => Promise<void>,
};

const fallbackColor: ColorOption = {
    name: 'orange',
    value: '#ff9b19',
};

export const useColorsStore = defineStore('colors', (): ColorsStore => {
    const colors = ref<ColorOption[]>([]);
    const defaultColorName = ref<string | null>(null);

    const defaultColor = computed<ColorOption>(() => {
        const colorByName = colors.value.find((target) => target.name === defaultColorName.value);

        return colorByName ?? colors.value[0] ?? fallbackColor;
    });

    const colorsDictionary = computed<Record<string, string>>(() => Object.fromEntries(
        colors.value.map(({ name, value }) => [name, value]),
    ));

    const hydrate = async () => {
        const { colors: colorsList, defaultColor: colorName } = await ColorsApi.getColors();
        colors.value = colorsList;
        defaultColorName.value = colorName;
    };

    return {
        colors: shallowReadonly(colors),
        defaultColor,
        colorsDictionary,
        hydrate,
    };
});
